import { selectCurrentPoi } from '@utils/poiHelper';
import React from 'react';
import Modal from '@roo/roo/Modal';
import Toast from '@roo/roo/Toast';
import { setStorage } from '@utils/localStorage';
import { printRawTagData } from '@utils/print/orderPrintService';
import * as LLog from '@utils/llogWrapper';

import { GRAY_AB_TOKEN, getGrayStateByToken } from '@utils/grayAB';
import rewritePathToUnified from '@utils/unifiedOrderPath';

import messageHubBridge, { MessageHubBridge } from '../../../services/messageHub/bridge';
import doLogout from './doLogout';
import { isTrustedFrame, sendMessageToSubFrame, FRAME_NAME } from './subFrames';
import pageJump from './pageJump';
import store from '../store';
import { IQueryMenuItem } from '../api/define';
import { isUnprocessedOrderType } from '../store/models/orderCount';
import feVersionPoller from '../connection/pull/poller/feVersion';
import bellService from '../service/bell';
import { BellConstructorConfig } from '../service/bell/Bell';
// eslint-disable-next-line import/no-cycle
import { serviceHallMessageHandler } from '../modules/CustomerService';

import '../assets/style/rooReset.scss';

interface ForwordMessageType {
    to: FRAME_NAME;
    data: any;
}

enum InformType {
    WARN = 'warn',
    WARNING = 'warning',
    ERROR = 'error',
    FAIL = 'fail',
    SUCCESS = 'success',
}

/**
 * 外层桥能力
 * 格式：{method: 'xxx',  args: 'xxx}
 * method: bridge api 见下方 BridgeType
 * args:  各方法入参
 *
 * demo:
 * parent.postMessage(JSON.stringify({'method': 'dialog', args: [{title: '弹框标题', content: '弹框内容'}]}))
 */

interface BridgeType extends MessageHubBridge {
    /** toast弹框 */
    inform: (params: { type: InformType; title: string; delay: number }) => void;
    /** 后退 */
    goBack: (params: { step: number }) => void;
    /** 页面跳转 */
    jumpTo: (params: { href: string; nojump?: boolean; selectLabel?: string }) => void;
    /** 外层弹框 */
    dialog: (params: { title: string; content: string }) => void;
    /** 选择指定门店 */
    selectWmPoi: (params: { wmPoiId: string; wmPoiName: string; jumpUrl: string }) => void;
    /** 给应用提供的全局共享数据 */
    getPlatformShareData: () => void;
    /** 平台主动push消息 */
    postMessage: (messageData: any) => void;
    /** 合同签署弹框 */
    signContract: (params: { noAutoReloadAfterSign: boolean; url: string }) => void;
    /** 取消签署 */
    cancelSignContract: () => void;
    /** 完成签署 */
    finishSignContract: () => void;
    /** 退出登录 */
    logon: () => void;
    /** 多个iframe之间转发消息 */
    forwordMessage: (params: ForwordMessageType) => void;
    /** 内层页面获取外层主页面的订单计数信息 */
    getOrderCountFromParent: () => void;
    /** 清空未读站内信计数 */
    clearUnReadNotificationCount: () => void;
    /** 静态资源版本更新检测 */
    checkFeVersionRefresh: (params: string) => void;
    /** 播放自定义铃声，支持设置优先级与循环次数 */
    playBell: (
        params: Pick<BellConstructorConfig, 'resourceUrl' | 'priority' | 'loopTimes'>,
    ) => void;
    /** 打印原始标签指令 */
    printRawTagData: (params: { templateDataList: string[] }) => void;
    /** 服务大厅回调 */
    serviceHallClose: (params: {
        /** 是否关闭服务大厅，默认关闭 */
        closable?: boolean;
        /** 1,打开URL，2，打开服体 */
        type: 1 | 2;
        /** url地址，type为1时需传递 */
        url?: string;
    }) => void;
}

let signContractDialog: any;
let noAutoReloadAfterSign = false; // 是否禁止协议签署后的页面自动刷新

function closeSignContractDialog() {
    if (signContractDialog) {
        signContractDialog.close();
        signContractDialog = false;
    }
}

/** 判断/queryMenu是否下发了商家首页 */
const hasIndexPageAccess = () => {
    const leftMenuList = store.getState().shellLeftMenu.leftMenuList;
    return leftMenuList.some((menu: IQueryMenuItem) => {
        if (menu.subMenus && menu.subMenus.length) {
            // 包含二级菜单则判断子菜单
            return menu.subMenus.some(
                subMenu => subMenu.url === '/v2/index' || subMenu.url === '/new_fe/business#/index',
            );
        } else {
            // 否则判断自身
            return menu.url === '/v2/index' || menu.url === '/new_fe/business#/index';
        }
    });
};

/** 找到左侧菜单里第一个可访问链接 */
const getFirstAccessableLink = () => {
    const leftMenuList = store.getState().shellLeftMenu.leftMenuList;
    const firstMenu = leftMenuList[0];
    if (firstMenu) {
        if (firstMenu.subMenus && firstMenu.subMenus.length) {
            return firstMenu.subMenus[0].url;
        } else {
            return firstMenu.url;
        }
    } else {
        // 异常场景，默认商家首页
        return '/new_fe/business#/index';
    }
};

const bridge: BridgeType = {
    inform: params => {
        if (params.type === 'warn') params.type = InformType.WARNING;
        if (params.type === 'error') params.type = InformType.FAIL;
        // eslint-disable-next-line waimaie/no-direct-toast-open
        Toast.open({
            status: params.type,
            title: params.title,
            duration: params.delay,
            position: 'center',
        });
    },
    goBack: params => {
        let step = -1;

        if (params && params.step !== undefined) {
            step = params.step;
        }
        window.history.go(step);
    },
    jumpTo: params => {
        let href = params.href;
        if (href) {
            // 明确传递跳转地址，如果是商家首页则兜底检测权限，否则直接跳转
            if (href === '/v2/index' || href === '/new_fe/business#/index') {
                // 检查是否有权限，如果没有权限，走兜底逻辑（第一个可跳转菜单）
                if (!hasIndexPageAccess()) {
                    href = getFirstAccessableLink();
                }
            }
        } else {
            // 未明确传递跳转地址，直接找菜单里第一个可跳转菜单
            href = getFirstAccessableLink();
        }

        if (params.nojump) {
            //改变hash，但不跳页面
            setStorage('nojump', 'true');
        }
        if (params.selectLabel) {
            //强制左侧选中
            // TODO 补充强制选中逻辑
            setStorage('selectLabel', params.selectLabel);
        }

        if (getGrayStateByToken(GRAY_AB_TOKEN.UNIFIED_ORDER)) {
            const newHref = rewritePathToUnified(href);
            if (newHref) {
                href = newHref;
            }
        }

        pageJump.go(href);
    },
    dialog: (params: { title: string; content: string }) => {
        Modal.confirm({
            title: params.title || '提示',
            children: params.content || '',
        });
    },
    // -- 选择指定门店
    selectWmPoi: params => {
        selectCurrentPoi(params.wmPoiId, params.wmPoiName, params.jumpUrl);
    },
    // -- 给应用提供的全局共享数据
    getPlatformShareData: () => {
        bridge.postMessage({
            type: 'platformShareData',
            data: window.CommConstants,
        });
    },
    postMessage: (messageData: any) => {
        sendMessageToSubFrame(FRAME_NAME.HASH, messageData);
    },
    signContract: (params: { noAutoReloadAfterSign: boolean; url: string }) => {
        noAutoReloadAfterSign = !!params.noAutoReloadAfterSign;
        signContractDialog = Modal.confirm({
            title: ' ',
            closable: true,
            showCancelButton: false,
            showConfirmButton: false,
            className: 'sign-contract-modal',
            children: (
                <iframe
                    title="合同签署弹框"
                    id="contractframe"
                    src={params.url}
                    width="100%"
                    height="100%"
                    frameBorder="no"
                    name="contractframe"
                />
            ),
        });
    },
    cancelSignContract: () => {
        closeSignContractDialog();
        bridge.postMessage({
            type: 'cancelSignContract',
        });
    },
    finishSignContract: () => {
        closeSignContractDialog();
        bridge.postMessage({
            type: 'finishSignContract',
        });

        if (noAutoReloadAfterSign) {
            // 页面不会自动刷新
        } else {
            window.location.reload();
        }
    },
    logon: () => {
        doLogout();
    },
    forwordMessage: params => {
        if (params.to) {
            sendMessageToSubFrame(params.to as FRAME_NAME, params.data);
        }
    },
    getOrderCountFromParent: () => {
        const storeData = store.getState();
        Object.keys(storeData.orderCount).forEach(key => {
            if (isUnprocessedOrderType(key)) {
                sendMessageToSubFrame(FRAME_NAME.HASH, {
                    handlerType: 'orderCountChangeFromParent',
                    [key]: storeData.orderCount[key],
                });
            }
        });
    },

    clearUnReadNotificationCount: () => {
        store.dispatch({
            type: 'shellHeader/updateUnReadNotificationCount',
            payload: 0,
        });
    },

    checkFeVersionRefresh: (targetVersion: string) => {
        feVersionPoller.checkIfNeedReload(targetVersion);
    },

    playBell: params => {
        const key = `temp-bell-priority${params.priority}-loop${params.loopTimes}-${params.resourceUrl}`;
        bellService.registerBell({ key, ...params });
        bellService.playBell(key);
    },
    printRawTagData: params => {
        printRawTagData(params.templateDataList);
    },
    serviceHallClose: params => {
        serviceHallMessageHandler(params);
    },
    ...messageHubBridge,
};

const dispatcher = (evt: MessageEvent) => {
    /** 是否来自智能客服弹框 */
    let isFromCustomerService = false;

    try {
        isFromCustomerService =
            evt.origin === 'https://h5.51ping.com' ||
            evt.origin === 'https://h5.dianping.com' ||
            evt.origin === 'http://h5.51ping.com' ||
            evt.origin === 'http://h5.dianping.com';
    } catch (e) {
        // do nothing
    }

    try {
        if (!isTrustedFrame(evt.source) && !isFromCustomerService) {
            return;
        }
    } catch (e) {
        // do nothing
    }

    // 验证数据的合法性
    const data = evt.data;
    try {
        LLog.info(
            {
                data,
            },
            LLog.REPORT_TAG_TYPE.BRIDGE_PLATFORM,
        );
    } catch (error) {
        // console.log('error',error)
    }
    let router;
    try {
        router = JSON.parse(data);
    } catch (e) {
        if (toString.call(data) === '[object Object]') {
            router = data;
        } else {
            return;
        }
    }
    if (isFromCustomerService) {
        // 来自智能客服iframe的页面跳转消息需要最小化窗口
        if (router.method === 'jumpTo' && window.csp && window.csp.miniChatEle) {
            window.csp.miniChatEle();
        }
    }
    const routerAPI = router.method;
    if (!routerAPI) {
        return;
    }
    // 功能的分发

    // @ts-ignore
    const _runner = bridge[routerAPI];

    if (_runner) {
        _runner(...(Array.isArray(router.args) ? router.args : []));
    }
};

const bindBridgeMessageListener = () => {
    window.addEventListener('message', dispatcher);
};

export default bindBridgeMessageListener;
export { bridge };
